import React, { useState, useEffect, useCallback, useRef } from "react";
import { useApp } from "../context/AppContext";

// Global Collaborator catalog browser.
// Not project-scoped — single shared cache from collaborator.pro.
// Reads from local DB (synced via /api/collaborator/sync background task).
export default function CollaboratorView() {
  const { apiFetch, t, dark, card, btnP, btnG, btnSm, stitle, muted, flash, inp } = useApp();

  const [status, setStatus] = useState(null);
  const [list, setList] = useState({ items: [], total: 0 });
  const [filters, setFilters] = useState({
    search: "", dr_min: "", dr_max: "", traffic_min: "", price_max: "",
    country: "", city: "", categories: [], dofollow: false, sort: "dr_desc",
  });
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState(null);
  const [checkedIds, setCheckedIds] = useState(new Set());
  const toggleCheck = (id, e) => { e.stopPropagation(); setCheckedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; }); };
  const toggleAll = () => setCheckedIds(prev => prev.size === list.items.length ? new Set() : new Set(list.items.map(x => x.id)));
  const copyChecked = () => {
    const items = list.items.filter(x => checkedIds.has(x.id));
    if (!items.length) { flash("Нічого не вибрано"); return; }
    const lines = items.map(it => [it.name || it.url, it.dr_num || "", it.organic_traffic_num ? Math.round(it.organic_traffic_num/1000)+"K" : "", it.price_min_uah ? Math.round(it.price_min_uah)+" UAH" : "", it.has_dofollow ? "dofollow" : "nofollow", it.categories || ""].join("\t"));
    const header = "Домен\tDR\tОрганіка\tЦіна від\tLink\tКатегорії";
    navigator.clipboard.writeText([header, ...lines].join("\n")).then(() => flash(`✓ Скопійовано ${items.length} рядків`)).catch(() => flash("Помилка копіювання"));
  };
  const [budget, setBudget] = useState("");
  const [linkCount, setLinkCount] = useState("");
  const [picking, setPicking] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [pickResult, setPickResult] = useState(null); // { selected, total_spent, remaining }
  const [syncing, setSyncing] = useState(false);
  const [task, setTask] = useState(null);
  const PAGE = 50;

  const loadStatus = useCallback(async () => {
    try { setStatus(await (await apiFetch(`/api/collaborator/status`)).json()); } catch (e) { flash("Помилка: " + e.message); }
  }, [apiFetch, flash]);

  const buildQs = useCallback((extra = {}) => {
    const p = new URLSearchParams();
    const f = { ...filters, ...extra };
    p.set("limit", f.limit || PAGE); p.set("offset", f.offset || 0); p.set("sort", f.sort || "dr_desc");
    if (f.search) p.set("search", f.search);
    if (f.dr_min) p.set("dr_min", f.dr_min);
    if (f.dr_max) p.set("dr_max", f.dr_max);
    if (f.traffic_min) p.set("traffic_min", f.traffic_min);
    if (f.price_max) p.set("price_max", f.price_max);
    if (f.country) p.set("country", f.country);
    if (f.city) p.set("city", f.city);
    if (f.dofollow) p.set("dofollow", "1");
    (f.categories || []).forEach(cat => p.append("categories", cat));
    return p.toString();
  }, [filters]);

  const loadList = useCallback(async () => {
    const qs = buildQs({ limit: PAGE, offset: page * PAGE });
    try { setList(await (await apiFetch(`/api/collaborator/sites?${qs}`)).json()); } catch {}
  }, [apiFetch, filters, page, buildQs]);

  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => { loadList(); }, [loadList]);


  const startSync = async () => {
    if (syncing) return;
    if (!window.confirm("Запустити повну синхронізацію каталогу Collaborator (~41,600 сайтів)? Це займе ~10-15 хв.")) return;
    setSyncing(true);
    setTask({ phase: "starting" });
    try {
      const r = await apiFetch(`/api/collaborator/sync`, { method: "POST" });
      const d = await r.json();
      if (!r.ok) { flash("Помилка: " + (d.error || "")); setSyncing(false); setTask(null); return; }
      const taskId = d.taskId;
      const poll = setInterval(async () => {
        try {
          const sR = await apiFetch(`/api/tasks/${taskId}`);
          const s = await sR.json();
          setTask({ ...(s.progress || {}), status: s.status });
          if (s.status === "completed" || s.status === "failed") {
            clearInterval(poll); setSyncing(false);
            await loadStatus(); await loadList();
            flash(s.status === "completed" ? `✓ Sync: ${(s.progress || {}).current || 0} сайтів` : "Помилка: " + (s.error || ""));
            setTimeout(() => setTask(null), 5000);
          }
        } catch {}
      }, 3000);
    } catch (e) { flash("Помилка: " + e.message); setSyncing(false); setTask(null); }
  };

  const exportCsv = async () => {
    try {
      const qs = buildQs({ limit: 5000, offset: 0 });
      const r = await apiFetch(`/api/collaborator/sites?${qs}`);
      const d = await r.json();
      const rows = d.items || [];
      if (!rows.length) { flash("Немає даних для експорту"); return; }
      const cols = ["name", "url", "dr_num", "ur", "traffic_num", "organic_traffic_num", "price_min_uah", "price_max_uah", "has_dofollow", "country", "placement_speed", "categories", "site_type", "site_age"];
      const lines = ["﻿" + cols.map(c => `"${c}"`).join(",")];
      for (const r of rows) {
        lines.push(cols.map(c => {
          const v = r[c];
          if (v == null) return "";
          return `"${String(v).replace(/"/g, '""').replace(/\n/g, " ")}"`;
        }).join(","));
      }
      const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `collaborator-catalog-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 100);
      flash(`✓ Експорт ${rows.length} рядків`);
    } catch (e) { flash("Помилка: " + e.message); }
  };

  const pickBudget = async () => {
    if (!budget) { flash("Введіть бюджет"); return; }
    setPicking(true);
    setPickResult(null);
    try {
      const r = await apiFetch("/api/collaborator/pick-budget", {
        method: "POST",
        body: JSON.stringify({
          budget_uah: parseFloat(budget) || 0,
          dr_min: filters.dr_min || undefined,
          dr_max: filters.dr_max || undefined,
          traffic_min: filters.traffic_min || undefined,
          price_max: filters.price_max || undefined,
          country: filters.country || undefined,
          city: filters.city || undefined,
          categories: filters.categories.length ? filters.categories : undefined,
          dofollow: filters.dofollow,
          count: linkCount ? parseInt(linkCount) : undefined,
        }),
      });
      const d = await r.json();
      if (d.error) { flash("Помилка: " + d.error); return; }
      setPickResult(d);
    } catch (e) { flash("Помилка: " + e.message); }
    setPicking(false);
  };

  // ── Render ──────────────────────────────────────────────────
  const lastSync = status?.state?.last_full_sync_at ? new Date(status.state.last_full_sync_at).toLocaleString("uk-UA") : null;
  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <h2 style={{ ...stitle, margin: 0 }}>🔵 Collaborator каталог</h2>
        <p style={{ ...muted, margin: "4px 0 0", fontSize: 13 }}>
          Глобальний каталог сайтів-донорів. Дані з collaborator.pro API.
        </p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 14 }}>
        <Stat label="Всього сайтів" value={status?.counts?.total ?? "—"} />
        <Stat label="DR ≥ 50" value={status?.counts?.dr_50_plus ?? "—"} color="#f59e0b" />
        <Stat label="DR ≥ 70" value={status?.counts?.dr_70_plus ?? "—"} color="#10b981" />
        <Stat label="З dofollow" value={status?.counts?.dofollow ?? "—"} />
        <Stat label="Min ціна" value={status?.counts?.min_price != null ? `${Math.round(status.counts.min_price)} ₴` : "—"} />
        <Stat label="Max ціна" value={status?.counts?.max_price != null ? `${Math.round(status.counts.max_price).toLocaleString()} ₴` : "—"} />
      </div>

      <div style={{ display: "flex", gap: 10, marginBottom: 14, alignItems: "center", flexWrap: "wrap" }}>
        <button onClick={startSync} disabled={syncing} style={{ ...btnP, opacity: syncing ? 0.5 : 1 }}>
          {syncing ? "Синхронізую..." : (lastSync ? "🔄 Оновити з API" : "📥 Синхронізувати каталог")}
        </button>
        <button onClick={exportCsv} style={{ ...btnG, fontSize: 12 }} title="Експорт відфільтрованих рядків у CSV (до 5000)">📥 CSV</button>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: "auto" }}>
          <input
            type="number"
            placeholder="Бюджет, ₴"
            value={budget}
            onChange={e => setBudget(e.target.value)}
            onKeyDown={e => e.key === "Enter" && pickBudget()}
            style={{ ...inp, width: 130, fontSize: 12, padding: "6px 10px" }}
          />
          <input
            type="number"
            placeholder="Ліміт (опц.)"
            value={linkCount}
            onChange={e => setLinkCount(e.target.value)}
            onKeyDown={e => e.key === "Enter" && pickBudget()}
            min="1"
            title="Макс. к-сть сайтів (необов\u2019язково — за замовч. заповнює весь бюджет)"
            style={{ ...inp, width: 75, fontSize: 12, padding: "6px 10px" }}
          />
          <button onClick={pickBudget} disabled={picking} style={{ ...btnP, opacity: picking ? 0.6 : 1, whiteSpace: "nowrap" }}>
            {picking ? "Підбираю…" : "🎯 Підібрати"}
          </button>
          {pickResult && (
            <button onClick={() => setPickResult(null)} style={{ ...btnG, fontSize: 12, padding: "6px 8px" }}>✕</button>
          )}
        </div>
        {checkedIds.size > 0 && <button onClick={copyChecked} style={{ ...btnP, fontSize: 12 }}>📋 Скопіювати {checkedIds.size}</button>}
        {lastSync && <span style={{ fontSize: 11, color: t.textMuted }}>Останній sync: {lastSync}</span>}
        {task && (
          <span style={{ fontSize: 12, color: t.textMuted }}>
            {task.phase === "syncing" ? `${task.current || 0}/${task.total || "?"} · сторінка ${task.page || 0}/${task.pageCount || 0}` :
              task.phase === "done" ? `✓ ${task.current || 0} сайтів` : task.phase || ""}
          </span>
        )}
      </div>

      {/* Filters */}
      <div style={{ ...card, padding: "14px 18px", marginBottom: 14, position: "relative", zIndex: 100 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
          <input type="text" placeholder="🔍 Пошук по домену/категорії" value={filters.search}
            onChange={e => { setFilters(f => ({ ...f, search: e.target.value })); setPage(0); }}
            style={{ ...inp, flex: 1, minWidth: 200, fontSize: 12 }}
          />
          <input type="number" placeholder="DR від" value={filters.dr_min}
            onChange={e => { setFilters(f => ({ ...f, dr_min: e.target.value })); setPage(0); }}
            style={{ ...inp, fontSize: 12, width: 80 }} min={0} max={100}
          />
          <input type="number" placeholder="DR до" value={filters.dr_max}
            onChange={e => { setFilters(f => ({ ...f, dr_max: e.target.value })); setPage(0); }}
            style={{ ...inp, fontSize: 12, width: 80 }} min={0} max={100}
          />
          <input type="number" placeholder="Трафік від" value={filters.traffic_min}
            onChange={e => { setFilters(f => ({ ...f, traffic_min: e.target.value })); setPage(0); }}
            style={{ ...inp, fontSize: 12, width: 110 }}
          />
          <input type="number" placeholder="Ціна до (UAH)" value={filters.price_max}
            onChange={e => { setFilters(f => ({ ...f, price_max: e.target.value })); setPage(0); }}
            style={{ ...inp, fontSize: 12, width: 130 }}
          />
          <select value={filters.country}
            onChange={e => { setFilters(f => ({ ...f, country: e.target.value, city: "" })); setPage(0); }}
            style={{ ...inp, fontSize: 12, minWidth: 140 }}>
            <option value="">Всі країни</option>
            <option value="Ukraine">🇺🇦 Україна</option>
            <option value="USA">🇺🇸 USA</option>
            <option value="France">🇫🇷 Франція</option>
            <option value="Poland">🇵🇱 Польща</option>
            <option value="Spain">🇪🇸 Іспанія</option>
            <option value="Netherlands">🇳🇱 Нідерланди</option>
            <option value="Italy">🇮🇹 Італія</option>
            <option value="Brazil">🇧🇷 Бразилія</option>
            <option value="Germany">🇩🇪 Німеччина</option>
            <option value="United Kingdom">🇬🇧 UK</option>
            <option value="India">🇮🇳 Індія</option>
            <option value="Turkey">🇹🇷 Туреччина</option>
            <option value="Bulgaria">🇧🇬 Болгарія</option>
            <option value="Romania">🇷🇴 Румунія</option>
            <option value="Argentina">🇦🇷 Аргентина</option>
            <option value="Kazakhstan">🇰🇿 Казахстан</option>
            <option value="Czech Republic">🇨🇿 Чехія</option>
            <option value="Lithuania">🇱🇹 Литва</option>
            <option value="Latvia">🇱🇻 Латвія</option>
            <option value="Hungary">🇭🇺 Угорщина</option>
            <option value="Sweden">🇸🇪 Швеція</option>
            <option value="Denmark">🇩🇰 Данія</option>
            <option value="Portugal">🇵🇹 Португалія</option>
            <option value="Greece">🇬🇷 Греція</option>
            <option value="Canada">🇨🇦 Канада</option>
            <option value="Australia">🇦🇺 Австралія</option>
            <option value="Azerbaijan">🇦🇿 Азербайджан</option>
            <option value="Georgia">🇬🇪 Грузія</option>
          </select>
          {filters.country === "Ukraine" && (
            <select value={filters.city}
              onChange={e => { setFilters(f => ({ ...f, city: e.target.value })); setPage(0); }}
              style={{ ...inp, fontSize: 12, minWidth: 140 }}>
              <option value="">Всі міста</option>
              <option value="Киев">Київ</option>
              <option value="Харьков">Харків</option>
              <option value="Одесса">Одеса</option>
              <option value="Днепр">Дніпро</option>
              <option value="Львов">Львів</option>
              <option value="Запорожье">Запоріжжя</option>
              <option value="Тернополь">Тернопіль</option>
              <option value="Ивано-Франковск">Івано-Франківськ</option>
              <option value="Ровно">Рівне</option>
              <option value="Житомир">Житомир</option>
              <option value="Черновцы">Чернівці</option>
              <option value="Полтава">Полтава</option>
              <option value="Чернигов">Чернігів</option>
              <option value="Винница">Вінниця</option>
              <option value="Луцк">Луцьк</option>
              <option value="Хмельницкий">Хмельницький</option>
              <option value="Сумы">Суми</option>
              <option value="Николаев">Миколаїв</option>
              <option value="Кропивницкий">Кропивницький</option>
            </select>
          )}
          <div style={{ position: "relative", zIndex: 9999 }}>
            <button onClick={() => setCatOpen(o => !o)} style={{ ...inp, fontSize: 12, minWidth: 180, textAlign: "left", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 10px" }}>
              <span>{filters.categories.length ? `Категорії (${filters.categories.length})` : "Всі категорії"}</span>
              <span style={{ fontSize: 10, opacity: 0.5 }}>{catOpen ? "▲" : "▼"}</span>
            </button>
            {catOpen && (
              <>
              <div onClick={() => setCatOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 9998 }} />
              <div style={{ position: "absolute", top: "100%", left: 0, marginTop: 4, zIndex: 9999, background: dark ? "#1a1a2e" : "#fff", border: `1px solid ${t.border}`, borderRadius: 8, boxShadow: "0 8px 32px rgba(0,0,0,0.25)", padding: "6px 4px", minWidth: 230, maxHeight: 340, overflowY: "auto" }}>
              {filters.categories.length > 0 && (
                <button onClick={() => { setFilters(f => ({ ...f, categories: [] })); setPage(0); }} style={{ ...btnSm, margin: "0 8px 4px", fontSize: 11, color: "#ef4444", borderColor: "#ef4444" }}>✕ Очистити</button>
              )}
              <label key="media (news)" style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", cursor: "pointer", borderRadius: 4, fontSize: 12 }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(99,102,241,0.08)"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <input type="checkbox" checked={filters.categories.includes("media (news)")}
                  onChange={e => { setFilters(f => ({ ...f, categories: e.target.checked ? [...f.categories, "media (news)"] : f.categories.filter(x => x !== "media (news)") })); setPage(0); }} />
                Медіа / Новини
              </label>
              <label key="business and finance" style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", cursor: "pointer", borderRadius: 4, fontSize: 12 }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(99,102,241,0.08)"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <input type="checkbox" checked={filters.categories.includes("business and finance")}
                  onChange={e => { setFilters(f => ({ ...f, categories: e.target.checked ? [...f.categories, "business and finance"] : f.categories.filter(x => x !== "business and finance") })); setPage(0); }} />
                Бізнес і фінанси
              </label>
              <label key="entertainment and hobbies" style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", cursor: "pointer", borderRadius: 4, fontSize: 12 }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(99,102,241,0.08)"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <input type="checkbox" checked={filters.categories.includes("entertainment and hobbies")}
                  onChange={e => { setFilters(f => ({ ...f, categories: e.target.checked ? [...f.categories, "entertainment and hobbies"] : f.categories.filter(x => x !== "entertainment and hobbies") })); setPage(0); }} />
                Розваги і хобі
              </label>
              <label key="technologies" style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", cursor: "pointer", borderRadius: 4, fontSize: 12 }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(99,102,241,0.08)"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <input type="checkbox" checked={filters.categories.includes("technologies")}
                  onChange={e => { setFilters(f => ({ ...f, categories: e.target.checked ? [...f.categories, "technologies"] : f.categories.filter(x => x !== "technologies") })); setPage(0); }} />
                Технології
              </label>
              <label key="city portals" style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", cursor: "pointer", borderRadius: 4, fontSize: 12 }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(99,102,241,0.08)"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <input type="checkbox" checked={filters.categories.includes("city portals")}
                  onChange={e => { setFilters(f => ({ ...f, categories: e.target.checked ? [...f.categories, "city portals"] : f.categories.filter(x => x !== "city portals") })); setPage(0); }} />
                Міські портали
              </label>
              <label key="sports and healthy nutrition" style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", cursor: "pointer", borderRadius: 4, fontSize: 12 }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(99,102,241,0.08)"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <input type="checkbox" checked={filters.categories.includes("sports and healthy nutrition")}
                  onChange={e => { setFilters(f => ({ ...f, categories: e.target.checked ? [...f.categories, "sports and healthy nutrition"] : f.categories.filter(x => x !== "sports and healthy nutrition") })); setPage(0); }} />
                Спорт і здоров'я
              </label>
              <label key="tourism and travel" style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", cursor: "pointer", borderRadius: 4, fontSize: 12 }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(99,102,241,0.08)"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <input type="checkbox" checked={filters.categories.includes("tourism and travel")}
                  onChange={e => { setFilters(f => ({ ...f, categories: e.target.checked ? [...f.categories, "tourism and travel"] : f.categories.filter(x => x !== "tourism and travel") })); setPage(0); }} />
                Туризм і подорожі
              </label>
              <label key="home and family" style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", cursor: "pointer", borderRadius: 4, fontSize: 12 }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(99,102,241,0.08)"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <input type="checkbox" checked={filters.categories.includes("home and family")}
                  onChange={e => { setFilters(f => ({ ...f, categories: e.target.checked ? [...f.categories, "home and family"] : f.categories.filter(x => x !== "home and family") })); setPage(0); }} />
                Дім і сім'я
              </label>
              <label key="fashion and beauty" style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", cursor: "pointer", borderRadius: 4, fontSize: 12 }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(99,102,241,0.08)"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <input type="checkbox" checked={filters.categories.includes("fashion and beauty")}
                  onChange={e => { setFilters(f => ({ ...f, categories: e.target.checked ? [...f.categories, "fashion and beauty"] : f.categories.filter(x => x !== "fashion and beauty") })); setPage(0); }} />
                Мода і краса
              </label>
              <label key="culture and art" style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", cursor: "pointer", borderRadius: 4, fontSize: 12 }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(99,102,241,0.08)"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <input type="checkbox" checked={filters.categories.includes("culture and art")}
                  onChange={e => { setFilters(f => ({ ...f, categories: e.target.checked ? [...f.categories, "culture and art"] : f.categories.filter(x => x !== "culture and art") })); setPage(0); }} />
                Культура і мистецтво
              </label>
              <label key="health and medicine" style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", cursor: "pointer", borderRadius: 4, fontSize: 12 }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(99,102,241,0.08)"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <input type="checkbox" checked={filters.categories.includes("health and medicine")}
                  onChange={e => { setFilters(f => ({ ...f, categories: e.target.checked ? [...f.categories, "health and medicine"] : f.categories.filter(x => x !== "health and medicine") })); setPage(0); }} />
                Здоров'я і медицина
              </label>
              <label key="electronics and technology" style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", cursor: "pointer", borderRadius: 4, fontSize: 12 }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(99,102,241,0.08)"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <input type="checkbox" checked={filters.categories.includes("electronics and technology")}
                  onChange={e => { setFilters(f => ({ ...f, categories: e.target.checked ? [...f.categories, "electronics and technology"] : f.categories.filter(x => x !== "electronics and technology") })); setPage(0); }} />
                Електроніка і техніка
              </label>
              <label key="education and science" style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", cursor: "pointer", borderRadius: 4, fontSize: 12 }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(99,102,241,0.08)"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <input type="checkbox" checked={filters.categories.includes("education and science")}
                  onChange={e => { setFilters(f => ({ ...f, categories: e.target.checked ? [...f.categories, "education and science"] : f.categories.filter(x => x !== "education and science") })); setPage(0); }} />
                Освіта і наука
              </label>
              <label key="marketing" style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", cursor: "pointer", borderRadius: 4, fontSize: 12 }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(99,102,241,0.08)"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <input type="checkbox" checked={filters.categories.includes("marketing")}
                  onChange={e => { setFilters(f => ({ ...f, categories: e.target.checked ? [...f.categories, "marketing"] : f.categories.filter(x => x !== "marketing") })); setPage(0); }} />
                Маркетинг
              </label>
              <label key="авто та мото" style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", cursor: "pointer", borderRadius: 4, fontSize: 12 }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(99,102,241,0.08)"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <input type="checkbox" checked={filters.categories.includes("авто та мото")}
                  onChange={e => { setFilters(f => ({ ...f, categories: e.target.checked ? [...f.categories, "авто та мото"] : f.categories.filter(x => x !== "авто та мото") })); setPage(0); }} />
                Авто та мото
              </label>
              <label key="construction and repair" style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", cursor: "pointer", borderRadius: 4, fontSize: 12 }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(99,102,241,0.08)"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <input type="checkbox" checked={filters.categories.includes("construction and repair")}
                  onChange={e => { setFilters(f => ({ ...f, categories: e.target.checked ? [...f.categories, "construction and repair"] : f.categories.filter(x => x !== "construction and repair") })); setPage(0); }} />
                Будівництво і ремонт
              </label>
              <label key="cooking" style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", cursor: "pointer", borderRadius: 4, fontSize: 12 }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(99,102,241,0.08)"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <input type="checkbox" checked={filters.categories.includes("cooking")}
                  onChange={e => { setFilters(f => ({ ...f, categories: e.target.checked ? [...f.categories, "cooking"] : f.categories.filter(x => x !== "cooking") })); setPage(0); }} />
                Кулінарія
              </label>
              <label key="internet" style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", cursor: "pointer", borderRadius: 4, fontSize: 12 }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(99,102,241,0.08)"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <input type="checkbox" checked={filters.categories.includes("internet")}
                  onChange={e => { setFilters(f => ({ ...f, categories: e.target.checked ? [...f.categories, "internet"] : f.categories.filter(x => x !== "internet") })); setPage(0); }} />
                Інтернет
              </label>
              <label key="cryptocurrencies" style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", cursor: "pointer", borderRadius: 4, fontSize: 12 }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(99,102,241,0.08)"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <input type="checkbox" checked={filters.categories.includes("cryptocurrencies")}
                  onChange={e => { setFilters(f => ({ ...f, categories: e.target.checked ? [...f.categories, "cryptocurrencies"] : f.categories.filter(x => x !== "cryptocurrencies") })); setPage(0); }} />
                Крипто
              </label>
              <label key="real estate" style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", cursor: "pointer", borderRadius: 4, fontSize: 12 }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(99,102,241,0.08)"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <input type="checkbox" checked={filters.categories.includes("real estate")}
                  onChange={e => { setFilters(f => ({ ...f, categories: e.target.checked ? [...f.categories, "real estate"] : f.categories.filter(x => x !== "real estate") })); setPage(0); }} />
                Нерухомість
              </label>
              <label key="furniture and interior" style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", cursor: "pointer", borderRadius: 4, fontSize: 12 }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(99,102,241,0.08)"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <input type="checkbox" checked={filters.categories.includes("furniture and interior")}
                  onChange={e => { setFilters(f => ({ ...f, categories: e.target.checked ? [...f.categories, "furniture and interior"] : f.categories.filter(x => x !== "furniture and interior") })); setPage(0); }} />
                Меблі і інтер'єр
              </label>
              <label key="seo" style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", cursor: "pointer", borderRadius: 4, fontSize: 12 }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(99,102,241,0.08)"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <input type="checkbox" checked={filters.categories.includes("seo")}
                  onChange={e => { setFilters(f => ({ ...f, categories: e.target.checked ? [...f.categories, "seo"] : f.categories.filter(x => x !== "seo") })); setPage(0); }} />
                SEO
              </label>
              <label key="lifestyle" style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", cursor: "pointer", borderRadius: 4, fontSize: 12 }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(99,102,241,0.08)"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <input type="checkbox" checked={filters.categories.includes("lifestyle")}
                  onChange={e => { setFilters(f => ({ ...f, categories: e.target.checked ? [...f.categories, "lifestyle"] : f.categories.filter(x => x !== "lifestyle") })); setPage(0); }} />
                Лайфстайл
              </label>
              <label key="politics" style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", cursor: "pointer", borderRadius: 4, fontSize: 12 }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(99,102,241,0.08)"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <input type="checkbox" checked={filters.categories.includes("politics")}
                  onChange={e => { setFilters(f => ({ ...f, categories: e.target.checked ? [...f.categories, "politics"] : f.categories.filter(x => x !== "politics") })); setPage(0); }} />
                Політика
              </label>
            </div>
              </>
            )}
          </div>
          <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
            <input type="checkbox" checked={filters.dofollow}
              onChange={e => { setFilters(f => ({ ...f, dofollow: e.target.checked })); setPage(0); }}
            /> лише dofollow
          </label>
          <select value={filters.sort} onChange={e => setFilters(f => ({ ...f, sort: e.target.value }))} style={{ ...inp, fontSize: 12 }}>
            <option value="dr_desc">DR ↓</option>
            <option value="dr_asc">DR ↑</option>
            <option value="traffic_desc">Трафік ↓</option>
            <option value="price_asc">Ціна ↑</option>
            <option value="price_desc">Ціна ↓</option>
            <option value="name_asc">Домен А→Я</option>
          </select>
          <span style={{ fontSize: 11, color: t.textMuted, marginLeft: "auto" }}>{list.total.toLocaleString()} результатів</span>
        </div>
      </div>

      {/* ── Budget pick result ── */}
      {pickResult && (
        <div style={{ ...card, padding: "16px 18px", marginBottom: 14, border: "1px solid rgba(99,102,241,0.4)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
            <div style={{ fontWeight: 700, fontSize: 15 }}>🎯 Підібрані сайти</div>
            <div style={{ fontSize: 12, padding: "4px 10px", borderRadius: 6, background: "rgba(16,185,129,0.12)", color: "#10b981", fontWeight: 600 }}>
              {pickResult.count} сайтів · {pickResult.total_spent.toLocaleString("uk-UA")} ₴
            </div>
            <div style={{ fontSize: 12, color: "#f59e0b", fontWeight: 600 }}>
              залишок: {pickResult.remaining.toLocaleString("uk-UA")} ₴
            </div>
            <button onClick={() => {
              const items = pickResult.selected;
              const lines = items.map(it => [it.name, it.dr_num||"", it.organic_traffic_num ? Math.round(it.organic_traffic_num/1000)+"K" : "", it.price_min_uah ? Math.round(it.price_min_uah)+" UAH" : ""].join("\t"));
              navigator.clipboard.writeText(["Домен\tDR\tОрганіка\tЦіна від", ...lines].join("\n")).then(() => flash("✓ Скопійовано")).catch(() => {});
            }} style={{ ...btnG, fontSize: 11, marginLeft: "auto" }}>📋 Копіювати все</button>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr>
                {["Домен","DR","Трафік","Ціна від","Лінк","Країна","Категорії"].map(h => (
                  <Th key={h}>{h}</Th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pickResult.selected.map((item, i) => (
                <tr key={item.id} style={{ borderBottom: `1px solid ${t.border}`, background: i % 2 ? (dark ? "rgba(255,255,255,.02)" : "rgba(0,0,0,.015)") : "transparent" }}>
                  <Td maxw={220}><a href={item.url} target="_blank" rel="noreferrer" style={{ color: "#0ea5e9", textDecoration: "none" }}>{item.name}</a></Td>
                  <Td><DRBadge dr={item.dr_num} /></Td>
                  <Td>{item.organic_traffic_num ? `${(item.organic_traffic_num/1000).toFixed(1)}K` : "—"}</Td>
                  <Td style={{ fontWeight: 700, color: "#0ea5e9" }}>{item.price_min_uah ? `${Math.round(item.price_min_uah).toLocaleString()} ₴` : "—"}</Td>
                  <Td>{item.has_dofollow ? <span style={{ color: "#10b981", fontWeight: 700 }}>do</span> : <span style={{ color: "#888" }}>no</span>}</Td>
                  <Td maxw={100}>{item.country || "—"}</Td>
                  <Td maxw={250} title={item.categories}>{trunc(item.categories, 60)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Table */}
      <div style={{ ...card, padding: 0, overflow: "hidden" }}>
        <div style={{ overflowX: "auto", maxHeight: "65vh", overflowY: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ position: "sticky", top: 0, background: t.bgCard, zIndex: 1 }}>
                <th style={{ padding: "6px 8px", width: 32, textAlign: "center" }}><input type="checkbox" checked={checkedIds.size === list.items.length && list.items.length > 0} onChange={toggleAll} style={{ cursor: "pointer" }} /></th>
                <Th>Домен</Th><Th width={50}>DR</Th><Th width={50}>UR</Th>
                <Th width={90}>Органіка</Th><Th width={100}>Ціна від</Th><Th width={70}>Тип лінку</Th>
                <Th width={100}>Країна</Th><Th width={100}>Швидкість</Th>
                <Th width={120}>Тип</Th><Th>Категорії</Th>
              </tr>
            </thead>
            <tbody>
              {list.items.map((item, i) => (
                <tr key={item.id} style={{
                  borderBottom: `1px solid ${t.border}`,
                  background: checkedIds.has(item.id) ? (dark ? "rgba(99,102,241,.12)" : "rgba(99,102,241,.06)") :
                              selected === item.id ? (dark ? "rgba(14,165,233,.12)" : "rgba(14,165,233,.06)") :
                              (i % 2 ? (dark ? "rgba(255,255,255,.02)" : "rgba(0,0,0,.015)") : "transparent"),
                  cursor: "pointer",
                }} onClick={() => setSelected(item.id === selected ? null : item.id)}>
                  <td style={{ padding: "4px 8px", textAlign: "center" }} onClick={e => toggleCheck(item.id, e)}><input type="checkbox" checked={checkedIds.has(item.id)} onChange={() => {}} style={{ cursor: "pointer" }} /></td>
                  <Td maxw={220}><a href={item.url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ color: "#0ea5e9", textDecoration: "none" }}>{item.name}</a></Td>
                  <Td><DRBadge dr={item.dr_num} /></Td>
                  <Td>{item.ur || "—"}</Td>
                  <Td>{item.organic_traffic_num ? `${(item.organic_traffic_num / 1000).toFixed(1)}K` : "—"}</Td>
                  <Td style={{ fontWeight: 700, color: "#0ea5e9" }}>{item.price_min_uah ? `${Math.round(item.price_min_uah).toLocaleString()} ₴` : "—"}</Td>
                  <Td>{item.has_dofollow ? <span style={{ color: "#10b981", fontWeight: 700 }}>do</span> : <span style={{ color: "#888" }}>no</span>}</Td>
                  <Td maxw={100}>{item.country || "—"}</Td>
                  <Td>{item.placement_speed || "—"}</Td>
                  <Td maxw={120}>{item.site_type || "—"}</Td>
                  <Td maxw={250} title={item.categories}>{trunc(item.categories, 50)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ display: "flex", justifyContent: "center", gap: 8, padding: 10, fontSize: 12 }}>
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} style={btnSm}>‹</button>
          <span style={{ alignSelf: "center", color: t.textMuted }}>стор. {page + 1} з {Math.ceil(list.total / PAGE) || 1}</span>
          <button onClick={() => setPage(p => p + 1)} disabled={(page + 1) * PAGE >= list.total} style={btnSm}>›</button>
        </div>
      </div>

      {/* Detail panel */}
      {selected && (() => {
        const item = list.items.find(x => x.id === selected);
        if (!item) return null;
        return (
          <div style={{ ...card, padding: "14px 18px", marginTop: 12 }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 12, gap: 8, flexWrap: "wrap" }}>
              <strong style={{ fontSize: 14 }}>{item.name}</strong>
              <DRBadge dr={item.dr_num} />
              {item.has_dofollow && <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 3, background: "rgba(16,185,129,.15)", color: "#10b981", fontWeight: 700 }}>dofollow</span>}
              <a href={item.url} target="_blank" rel="noreferrer" style={{ ...btnSm, fontSize: 11, textDecoration: "none", color: "#0ea5e9", borderColor: "rgba(14,165,233,.3)" }}>↗ Collaborator</a>
              <a href={`https://${item.name}`} target="_blank" rel="noreferrer" style={{ ...btnSm, fontSize: 11, textDecoration: "none" }}>↗ Відкрити сайт</a>
              <button onClick={() => setSelected(null)} style={{ ...btnSm, marginLeft: "auto" }}>✕</button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12 }}>
              <Field label="DR / UR" value={`${item.dr || "—"} / ${item.ur || "—"}`} />
              <Field label="Trust Flow / CF" value={`${item.tf || "—"} / ${item.cf || "—"}`} />
              <Field label="Trust Ratio" value={item.tr || "—"} />
              <Field label="Moz DA" value={item.da_moz || "—"} />
              <Field label="Ahrefs Rank" value={item.ahrefs_rank || "—"} />
              <Field label="Органічний трафік" value={item.organic_traffic || "—"} />
              <Field label="Схожий трафік" value={item.traffic || "—"} />
              <Field label="Беклінки" value={item.backlinks || "—"} />
              <Field label="Реф. домени" value={item.referral_domains || "—"} />
              <Field label="Ключові слова" value={item.keywords || "—"} />
              <Field label="AI Overview" value={item.google_ai_overview || "—"} />
              <Field label="Вік сайту" value={item.site_age?.trim() || "—"} />
              <Field label="Додано в систему" value={item.added_to_system?.trim() || "—"} />
              <Field label="Країна" value={item.country || "—"} />
              <Field label="Швидкість" value={item.placement_speed || "—"} />
              <Field label="Тип сайту" value={item.site_type || "—"} />
              <Field label="Обмін лінками" value={item.link_exchange || "—"} />
            </div>

            <div style={{ marginTop: 14 }}>
              <strong style={{ fontSize: 11, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.5 }}>Категорії</strong>
              <div style={{ fontSize: 12, marginTop: 4 }}>{item.categories || "—"}</div>
            </div>

            {Array.isArray(item.prices) && item.prices.length > 0 && (
              <div style={{ marginTop: 14 }}>
                <strong style={{ fontSize: 11, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.5 }}>Ціни розміщення</strong>
                <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 6 }}>
                  {item.prices.map((p, i) => (
                    <div key={i} style={{ padding: 8, borderRadius: 6, background: dark ? "rgba(255,255,255,.03)" : "rgba(0,0,0,.02)", fontSize: 12, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                      <strong>{p.name}</strong>
                      <span><span style={{ color: t.textMuted }}>Публікація:</span> {p.pricePublication || "—"}</span>
                      <span><span style={{ color: t.textMuted }}>Написання:</span> {p.priceSpelling || "—"}</span>
                      <span><span style={{ color: t.textMuted }}>Анонс:</span> {p.priceAnnouncement || "—"}</span>
                      <span style={{ color: p.linkType === "dofollow" ? "#10b981" : "#f59e0b", fontWeight: 700 }}>{p.linkType}</span>
                      <span><span style={{ color: t.textMuted }}>Посилань:</span> {p.numberOfLinks ?? "—"}</span>
                      {p.priceContrCategories && <span><span style={{ color: t.textMuted }}>+ дод. категорії:</span> {p.priceContrCategories}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

function Stat({ label, value, color }) {
  const { t, card } = useApp();
  return (
    <div style={{ ...card, padding: "10px 12px" }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: color || t.text, marginTop: 2 }}>{value}</div>
    </div>
  );
}
function Field({ label, value }) {
  const { t } = useApp();
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 600, marginTop: 2 }}>{value}</div>
    </div>
  );
}
function Th({ children, width }) {
  const { t } = useApp();
  return <th style={{ padding: "8px 10px", textAlign: "left", fontSize: 10, fontWeight: 700, color: t.textMuted, borderBottom: `2px solid ${t.border}`, textTransform: "uppercase", whiteSpace: "nowrap", width: width || "auto" }}>{children}</th>;
}
function Td({ children, maxw, title, style }) {
  return <td title={title} style={{ padding: "6px 10px", maxWidth: maxw || "auto", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", ...style }}>{children}</td>;
}
function DRBadge({ dr }) {
  const v = Math.round(dr || 0);
  const bg = v >= 70 ? "#10b981" : v >= 50 ? "#f59e0b" : v >= 30 ? "#6366f1" : "#888";
  return <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 3, background: bg + "22", color: bg, minWidth: 28, display: "inline-block", textAlign: "center" }}>{dr != null ? v : "—"}</span>;
}
function trunc(s, n) {
  if (!s) return <span style={{ color: "#888" }}>—</span>;
  return s.length > n ? s.slice(0, n) + "…" : s;
}
